import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { ViewState } from '../types';
import { useAuth } from './AuthContext';
import { isClockOnlyRole } from '../constants/roles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIdioma } from '../i18n/idioma';
import { tituloDeVista } from '../i18n/titulosDeVista';
import { esRutaPublica } from '../utils/rutas';

interface NavigationContextType {
    currentView: ViewState;
    navigateTo: (view: ViewState) => void;
    /** ¿Puede esta persona llegar a esa vista? La misma pregunta que hace
     *  `navigateTo`, para que la interfaz no ofrezca lo que no se puede abrir. */
    canNavigate: (view: ViewState) => boolean;
    activeProposalId: string | null;
    viewProposal: (id: string) => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    openSettings: (tab?: string) => void;
    closeSettings: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
};

// Map URL pathnames back to the ViewState string for TopNavigation / Sidebar active states.
// Every path in viewToPathMap MUST have an entry here or the nav highlights the
// wrong item — an unmapped path silently falls back to 'Dashboard'.
// navigationMaps.test.ts enforces this.
export const pathToViewMap: Record<string, ViewState> = {
    '/': 'Dashboard',
    '/dashboard': 'Dashboard',
    '/clients': 'Clients',
    '/estimates': 'Estimates',
    '/projects': 'Projects',
    '/finance': 'Finance',

    '/schedule': 'Calendar',
    '/materials': 'Pricebook',
    '/templates': 'Templates',


    '/inventory': 'Inventory',

    '/settings': 'Settings',
    '/design-showcase': 'DesignShowcase',
    '/proposal': 'ProposalViewer',

    '/follow-ups': 'FollowUps',
    '/services': 'Services',
    '/timeclock': 'TimeClock',
    '/workforce': 'Workforce',
    '/classes': 'Classes',
    '/ai-assistant': 'AIAssistant'
};

// Reverse map to navigate via strings.
// Note: 'Leads' redirects to /clients because Leads is now nested as a sub-tab inside ClientsView.
// 'Maintenance' has no dedicated route; left pointing at /dashboard as a safe fallback.
export const viewToPathMap: Record<ViewState, string> = {
    'Dashboard': '/dashboard',
    'Leads': '/clients',
    'Clients': '/clients',
    'Estimates': '/estimates',
    'Projects': '/projects',
    'Finance': '/finance',


    'Calendar': '/schedule',
    'Pricebook': '/materials',
    'Templates': '/templates',

    'ProposalViewer': '/proposal',

    'Settings': '/settings',
    'DesignShowcase': '/design-showcase',

    'FollowUps': '/follow-ups',

    'TimeClock': '/timeclock',
    'Services': '/services',
    'Workforce': '/workforce',
    'Inventory': '/inventory',
    'Maintenance': '/dashboard',
    'Classes': '/classes',
    'AIAssistant': '/ai-assistant'
};

/**
 * Views every signed-in user may reach regardless of `allowed_views`.
 *
 * The alternative was to append 'Classes' to all 195 rows of
 * workspace_users.allowed_views, and that is a trap: 7 users currently have an
 * EMPTY array, and the guard below is skipped entirely when the array is empty.
 * Adding one view to those rows would switch their guard ON and restrict them
 * to that single view — locking them out of screens they use today.
 *
 * Settings/DesignShowcase/ProposalViewer/AIAssistant were already exempt inline;
 * this collects them so the two places that check stay in step.
 */
export const ALWAYS_ALLOWED_VIEWS: ViewState[] = [
    'Settings', 'DesignShowcase', 'ProposalViewer', 'AIAssistant', 'Classes',
];

/** La vista que corresponde a una ruta. La propuesta pública no tiene entrada
 *  en el mapa (`/public/...`) pero es el visor de propuestas. */
const vistaDe = (pathname: string): ViewState => {
    if (pathname.startsWith('/public/proposal/')) return 'ProposalViewer';
    const basePath = '/' + (pathname.split('/')[1] || '');
    return pathToViewMap[basePath] || 'Dashboard';
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthorized, currentUser, allowedViews, userRole, isSuperAdmin, clientId } = useAuth();
    const [activeProposalId, setActiveProposalId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // React Router Hooks
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useIdioma();

    /* ¿Puede esta persona llegar a esa vista?
       La misma pregunta que hacen el guardia de rutas de abajo y `navigateTo`,
       en un solo sitio, para que la interfaz pueda NO ofrecer lo que no se
       puede abrir: una fila del calendario o un botón «Volver a estimados» que
       no hacían nada al tocarlos se leían como la app colgada — 49 avisos
       «Access denied» en dos semanas, todos de toques repetidos.

       'ProposalViewer' es el caso especial. Está en ALWAYS_ALLOWED_VIEWS por la
       propuesta PÚBLICA (`/public/proposal/:id`), que cualquiera con sesión
       puede abrir. Pero el mismo nombre de vista cubre el EDITOR interno
       (`/proposal/:id`), que enseña precios y márgenes: ese sólo se abre con
       'Estimates' — o con 'ProposalViewer' concedido a mano en la invitación
       (TeamMemberModal lo ofrece como «Propuestas»)—. Sin esto un capataz
       llegaba al editor desde la ficha de la obra (EST #) o desde la del
       cliente, saltándose `canSeeFinancials`. */
    const puedeAbrirPropuestas = allowedViews.includes('Estimates') || allowedViews.includes('ProposalViewer');
    const sinGuardia = !isAuthorized || isSuperAdmin || !!clientId;
    const canNavigate = (view: ViewState): boolean => {
        if (sinGuardia) return true;
        if (view === 'ProposalViewer') return puedeAbrirPropuestas;
        return allowedViews.includes(view) || ALWAYS_ALLOWED_VIEWS.includes(view);
    };
    /** El nombre que se le dice a la persona. El editor de propuestas no tiene
     *  título propio: lo que le falta es «Estimados». */
    const nombreDeVista = (view: ViewState): string =>
        tituloDeVista(view === 'ProposalViewer' ? 'Estimates' : view, t) || view;
    const avisarSinAcceso = (view: ViewState) => {
        // Se DICE, en vez de no hacer nada: un toque que no responde se lee como
        // la app colgada y se repite (27 veces seguidas, en el registro). Info y
        // no warn: no es un fallo, es el guardia haciendo su trabajo.
        console.info(`[Navigation] sin acceso a ${view}`);
        toast(t('nav.sinAcceso', { vista: nombreDeVista(view) }), { id: 'nav-sin-acceso', icon: '🔒' });
    };

    // Derive the ViewState logically from the current React Router path.
    // Desde la ruta ya en el primer render, no 'Dashboard' hasta que corra el
    // efecto: el guardia de abajo corre en ese mismo primer commit y, con
    // sesión ya resuelta, veía 'Dashboard' en vez de la ruta abierta y mandaba
    // a quien no tiene Panel (un capataz) a su primera vista permitida —
    // cualquier enlace directo se perdía.
    const [currentView, setCurrentView] = useState<ViewState>(() => vistaDe(location.pathname));
    /** Last view we reacted to, so the search reset fires on real view changes
     *  only — not on every re-render or same-view path change (/projects/:id). */
    const prevViewRef = useRef<ViewState>(currentView);

    useEffect(() => {
        const pathSegments = location.pathname.split('/');
        const mappedView = vistaDe(location.pathname);

        // Handle public proposals slightly differently if needed
        if (location.pathname.startsWith('/public/proposal/')) {
            const pId = pathSegments[3];
            if (pId && pId !== activeProposalId) {
                setActiveProposalId(pId);
            }
        }

        // P-1: the internal proposal editor route now carries the id (/proposal/:id)
        // so a hard refresh / deep-link / bookmark restores the active proposal
        // instead of dropping it (activeProposalId was previously state-only).
        if (pathSegments[1] === 'proposal') {
            const pId = pathSegments[2];
            if (pId && pId !== activeProposalId) {
                setActiveProposalId(pId);
            }
        }

        // The search box is app-wide state. Without this reset a term typed on
        // one screen silently keeps filtering the next one — a user who leaves
        // Clients with "Saul" in the box comes back to a list that looks empty
        // with no visible cause. Clear it whenever the view actually changes.
        if (prevViewRef.current !== mappedView) {
            prevViewRef.current = mappedView;
            setSearchQuery('');
        }

        setCurrentView(mappedView);
    }, [location.pathname]);

    useEffect(() => {
        // Rol de solo-reloj: una sola pantalla, y el guardia va ANTES que la
        // lista de allowed_views. Esa columna se edita a mano al invitar y es
        // fácil marcar una casilla de más; el rol es la garantía, así que una
        // invitación mal puesta no puede abrir la app. Tampoco hay exenciones
        // (Settings, ProposalViewer…): "nada más que el reloj" es literal.
        // Classes is the ONE exception to "nothing but the clock". That rule
        // exists to keep BUSINESS information away from a crew account — jobs,
        // clients, pricing, the assistant. A recorded training class is not
        // business information; it is the thing that teaches a labourer to use
        // the clock they are being sent here for. Nothing else is exempt.
        if (currentUser && isAuthorized && isClockOnlyRole(userRole)
            && currentView !== 'TimeClock' && currentView !== 'Classes') {
            navigate('/timeclock', { replace: true });
            return;
        }

        /* La lista VACÍA ya no abre la puerta — pero con DOS excepciones que
           casi cuestan un encierro.

           Esto decía `allowedViews.length > 0`, así que a quien llegara con la
           lista vacía —un `allowed_views` NULL en la base, un rol mal puesto—
           se le saltaba el guardia ENTERO: escribiendo la dirección alcanzaba
           /finance, /materials, /dashboard y /workforce. Y de esas, Precios y
           Panel no comprueban el rol por su cuenta.

           La primera versión de este arreglo se apoyaba en que «isAuthorized y
           allowedViews se fijan juntos, así que la lista vacía significa que no
           tiene permisos». Es FALSO, y lo cazó una auditoría: hay dos caminos en
           `resolvePermissions` que salen a propósito con la lista vacía y la
           sesión autorizada —el SUPERADMINISTRADOR (AuthContext.tsx:76-84, que
           vive en su propio panel aislado) y el PORTAL DEL CLIENTE, que navega
           por sus propias rutas—. Para ellos la lista vacía no es «no sé qué
           puede ver»: es «esto no se gobierna con esta lista». Sin la exención,
           el guardia los mandaba al reloj de fichar.

           Para todos los demás, la lista vacía sí es un aviso, y el destino es
           el reloj y no el Panel: si no sabemos qué puede ver esta persona, lo
           que toca es lo que no filtra nada. */
        if (currentUser && isAuthorized && !isSuperAdmin && !clientId) {
            // El editor interno de propuestas NO está exento (ver `canNavigate`
            // arriba); la propuesta pública sí. Escribir la dirección a mano no
            // puede saltarse lo que el botón ya no ofrece.
            const editorInterno = currentView === 'ProposalViewer' && !esRutaPublica(location.pathname);
            const permitida = editorInterno
                ? puedeAbrirPropuestas
                : allowedViews.includes(currentView) || ALWAYS_ALLOWED_VIEWS.includes(currentView);
            if (!permitida) {
                const firstAllowedPath = allowedViews.length > 0
                    ? (viewToPathMap[allowedViews[0]] || '/timeclock')
                    : '/timeclock';
                navigate(firstAllowedPath, { replace: true });
            }
        }
    }, [currentUser?.uid, isAuthorized, allowedViews, currentView, location.pathname, navigate, userRole]);

    const navigateTo = (view: ViewState) => {
        // Mismo agujero que arriba: con la lista vacía, esta guarda no miraba
        // nada. `canNavigate` exige `isAuthorized` para no bloquear la
        // navegación mientras los permisos todavía se resuelven.
        if (!canNavigate(view)) {
            avisarSinAcceso(view);
            return;
        }

        // 'Leads' no tiene ruta propia: es una pestaña dentro de /clients, y sin
        // el parámetro se cae en la pestaña de Clientes. El mapa guarda rutas
        // (pathToViewMap las compara contra location.pathname, que nunca trae
        // query), así que la pestaña se añade aquí.
        if (view === 'Leads') {
            navigate('/clients?tab=leads');
            return;
        }

        const path = viewToPathMap[view] || '/dashboard';
        navigate(path);
    };

    const viewProposal = (id: string) => {
        // El editor enseña precios: misma puerta que `navigateTo('ProposalViewer')`.
        if (!canNavigate('ProposalViewer')) {
            avisarSinAcceso('ProposalViewer');
            return;
        }
        setActiveProposalId(id);
        // P-1: put the id in the URL so refresh/deep-link survives.
        navigate(`/proposal/${id}`);
    };

    const openSettings = (tab?: string) => {
        if (tab) navigate(`/settings?tab=${encodeURIComponent(tab)}`);
        else navigate('/settings');
    };
    const closeSettings = () => navigate('/dashboard');

    return (
        <NavigationContext.Provider value={{
            currentView,
            navigateTo,
            canNavigate,
            activeProposalId,
            viewProposal,
            searchQuery,
            setSearchQuery,
            openSettings,
            closeSettings
        }}>
            {children}
        </NavigationContext.Provider>
    );
};
